import { PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useFirstTimeSeen } from '@/hooks/useFirstTimeSeen'

type CoachPopoverProps = {
  /**
   * Used to circumvent layout issues when using Tooltip inside a Popover by only rendering the Tooltip after acknowledgment.
   */
  childrenAfterAcknowledgement?: React.ReactNode
  coachKey: string
  description: string
  disabled?: boolean
  title: string
}

export const CoachPopover = ({
  children,
  childrenAfterAcknowledgement,
  coachKey,
  description,
  disabled = false,
  title,
}: PropsWithChildren<CoachPopoverProps>) => {
  const { isFirstTime, markAsSeen } = useFirstTimeSeen(coachKey)
  const { t } = useTranslation()

  if (!isFirstTime) {
    return childrenAfterAcknowledgement ?? children
  }

  return (
    <Popover open={isFirstTime && !disabled}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="flex flex-col gap-3 shadow-lg" side="bottom">
        <div>
          <p className="font-medium text-sm">{title}</p>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <Button className="self-end" onClick={markAsSeen} size="sm" variant="outline">
          {t('global.iAcknowledge')}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
